import { FORM_SUBMISSION_OUTCOMES } from "@njbsoft/api-client";
import { type operations } from "@njbsoft/types/customer";
import { type SubmissionPageParams } from "@/hooks/use-customer-tests";
import { dateParam, enumParam, pageParam, uuidParam } from "./search-params";

type SubmissionQuery = NonNullable<operations["listCustomerSubmissions"]["parameters"]["query"]>;

export type HistorySort = NonNullable<SubmissionQuery["sort_by"]>;
export type HistoryDirection = NonNullable<SubmissionQuery["sort_dir"]>;
export type HistoryResult = NonNullable<SubmissionQuery["outcome"]>;

/** History's sorts, each a column id and an API `sort_by`. */
export const HISTORY_SORTS: readonly HistorySort[] = ["date", "device", "property", "result"];

export const HISTORY_DIRECTIONS: readonly HistoryDirection[] = ["asc", "desc"];

export const HISTORY_PAGE_SIZE = 25;

/** `/testing/history`' search: the filters, the sort and the page, every one sent to the server. */
export interface HistorySearch {
  /** A facility id. */
  property?: string;
  result?: HistoryResult;
  /** The first test day, `YYYY-MM-DD`. */
  from?: string;
  /** The last test day, `YYYY-MM-DD`, never earlier than `from`. */
  to?: string;
  /** Absent reads as `date`. */
  sort?: HistorySort;
  /** Absent reads as `desc`. */
  dir?: HistoryDirection;
  /** A page past the first; the first page is the param's absence. */
  page?: number;
}

// The params the search does not hold are left out rather than carried as
// undefined, so the search reads as the URL does and the wire gets no empty key.
function withoutUndefined<T extends object>(value: T): T {
  // Dropping the undefined entries leaves every remaining key as `T` types it.
  return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as T;
}

/**
 * Each param through its validator; anything else, and any value a param
 * cannot hold, is dropped. A `to` earlier than `from` is dropped too: the range
 * would match nothing, and the server would only be asked to say so.
 */
export function validateHistorySearch(search: Record<string, unknown>): HistorySearch {
  const from = dateParam(search.from);
  const to = dateParam(search.to);
  return withoutUndefined<HistorySearch>({
    property: uuidParam(search.property),
    result: enumParam<HistoryResult>(search.result, FORM_SUBMISSION_OUTCOMES),
    from,
    to: to !== undefined && from !== undefined && to < from ? undefined : to,
    sort: enumParam(search.sort, HISTORY_SORTS),
    dir: enumParam(search.dir, HISTORY_DIRECTIONS),
    page: pageParam(search.page),
  });
}

/**
 * The history read's query for a search at an account: every filter under its
 * wire name, the sort always sent (its default filled in), and the page as an
 * offset into pages of `HISTORY_PAGE_SIZE`.
 */
export function historyQueryParams(search: HistorySearch, accountId: string): SubmissionPageParams {
  return withoutUndefined<SubmissionPageParams>({
    account_id: accountId,
    facility_id: search.property,
    outcome: search.result,
    performed_from: search.from,
    performed_to: search.to,
    sort_by: search.sort ?? "date",
    sort_dir: search.dir ?? "desc",
    limit: HISTORY_PAGE_SIZE,
    offset: ((search.page ?? 1) - 1) * HISTORY_PAGE_SIZE,
  });
}
